/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
// import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Stack, Box } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm, FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  ICandidate,
  uploadFileToS3,
  CandidateResume,
  IJob,
  newMappingEntity,
  IMappingFormSettingsResponse,
  checkExistingCandidates,
  saveCandidate,
  saveDocuments,
} from 'wisestep-api';
import {
  WsButton,
  WsDrawer,
  IMessageAndInteractionPipeline,
  WsTypography,
  WsTypoVariants,
  IUserDTO,
  WsColors,
  WsListingLoader,
} from 'wisestep-base';

import { useUserContext } from '../../../context';
import { cacheJob } from '../../../utils/cacheService';
import { success, failed, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import { SourceType } from '../../candidates/components/candidateBulkSelectionBar/candidateBulkSelectionBar';
import MapAsSubmission from '../../jobs/listing/components/mapAsSubmission/MapAsSubmission';
import {
  amplitudeEventJobCrmSmartSourceAiMapAsLeadSave,
  amplitudeEventJobCrmSmartSourceSimilarJobsMapAsLeadSave,
} from '../../jobsCrm/utils/AmplitudeEvents';
import conversationWidgetStore from '../store/conversationWidgetStore';
import IOtherDocument from '../types/IOtherDocument';
import { RESUME_ACTIONS } from '../types/ResumeActionTypes';
import { downloadCV, trackCV } from '../utils/cvHelper';
import HeaderAndCloseButtom from '../utils/HeaderAndCloseButtom';
import { createMappingEntity, saveMapping } from '../utils/saveMapping';

import ActionFooter from './ActionFooter';
import CVViewModal from './CVViewModal';
import PersonalInfoNew from './PersonalInfoNew';
import WidgetJobManagement from './WidgetJobManagement';

interface IUpdateProfile {
  openUpdatePanel: boolean;
  pipeline?: IMessageAndInteractionPipeline;
  candidate: ICandidate | null;
  downloadSource?: string | null;
  closeUpdatePanel: () => void;
  job?: IJob;
  mapAsLead?: boolean;
  showProfile?: boolean;
  hideJobSelection?: boolean;
  isFrom?: string;
  heading?: string;
  mappingFormSettings?: IMappingFormSettingsResponse;
  duplicateMapping?: boolean;
  isSubmit?: boolean;
}

const UpdateProfileNew: FC<IUpdateProfile> = ({
  openUpdatePanel = false,
  candidate,
  closeUpdatePanel,
  job,
  mapAsLead = false,
  showProfile = true,
  hideJobSelection = false,
  isFrom,
  heading,
  duplicateMapping = false,
  isSubmit = false,
}) => {
  const navigate = useNavigate();
  const [candidateEntity, setCandidateEntity] = useState<ICandidate | null>(candidate);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [openCvView, setOpenCvView] = useState<boolean>(false);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const cvViewSourcePage = useConversationWidgetStore((state) => state.cvViewSourcePage);
  const openModalWithAction = useConversationWidgetStore((state) => state.openModalWithAction);
  const activityConversation = useConversationWidgetStore((state) => state.activityConversation);
  const [resumeURl, setIsResumeURL] = useState<string | null>(null);
  const [candidateResumes, setCandidateResumes] = useState<CandidateResume[]>([]);
  const [otherDocument, setOtherDocument] = useState<IOtherDocument[]>([]);
  const [openSubmissionForm, setOpenSubmissionForm] = useState<boolean>(false);
  job && conversationWidgetStore((state) => state.updateSelectedJob)(job);

  const disableMapping = !activityConversation
    ? candidate?.jobId && candidate.jobId === job?.id && candidate?.isJobCandidateMapping === false
    : false;

  const methods = useForm();

  const {
    handleSubmit,
    register,
    setValue,
    formState: { isSubmitting },
  } = methods;
  const [isCandidate, setIsCandidate] = useState<
    'PRESENT' | 'NOT PRESENT' | 'UNDEFINED' | 'DUPLICATE'
  >('UNDEFINED');
  const [isResume, setIsResume] = useState(true);

  useEffect(() => {
    if (candidateEntity != null && isResume) setIsCandidate('PRESENT');
  }, [candidateEntity, isResume]);
  useEffect(() => {
    if (disableMapping || duplicateMapping) setIsCandidate('DUPLICATE');
  }, [disableMapping, duplicateMapping]);

  register('email', {
    onBlur: async (e) => {
      if (!isEmpty(e.target.value)) {
        const validate = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,6})+$/.test(e.target.value);
        if (validate) {
          await setTimeout(() => setValue('email', e.target.value), 1000);
          try {
            const candidateDetails = await checkExistingCandidates(e.target.value);
            if (candidateDetails?.data) {
              setCandidateEntity(candidateDetails?.data);
              setIsCandidate('PRESENT');
            } else {
              setIsCandidate('NOT PRESENT');
            }
          } catch (e) {
            setIsCandidate('NOT PRESENT');
          }
        } else {
          await setTimeout(() => setValue('email', ''), 200);
          setNotification(warning('Please enter valid email'));
          setIsCandidate('UNDEFINED');
        }
      } else {
        setIsCandidate('UNDEFINED');
        setNotification(warning('Please enter the email'));
      }
    },
  });

  useEffect(() => {
    if (candidate?.email) {
      setValue('email', candidate?.email);
    }
  }, [candidate?.email, setValue]);

  useEffect(() => {
    setCandidateEntity(candidate);
    setOpenDrawer(openUpdatePanel);
  }, [candidate, openUpdatePanel]);

  const onFormSubmit = async (data: FieldValues) => {
    try {
      amplitudeIntegration();

      if (!job?.id) {
        setDisableSubmit(false);
        setNotification(warning('Please select a job first'));
        return;
      }
      const hasRequiredFields = () => {
        if (isCandidate !== 'NOT PRESENT' && !isEmpty(data.email)) return true;
        if (
          isCandidate === 'NOT PRESENT' &&
          !isEmpty(data.email) &&
          !isEmpty(candidateEntity?.resumeLocation)
        ) {
          return true;
        }
        return false;
      };

      if (!hasRequiredFields()) {
        setDisableSubmit(false);
        setNotification(
          warning(
            isCandidate === 'NOT PRESENT'
              ? 'Please enter email and attach resume'
              : 'Please enter email'
          )
        );
        return;
      }

      if (!isSubmit) {
        const candidateData =
          isCandidate !== 'PRESENT' ? createMappingEntity(data, candidateEntity, job) : null;
        if (!candidateData) {
          if (candidateResumes.length > 0) {
            try {
              await saveDocuments(candidateResumes);
            } catch (e) {
              console.error(e);
              setNotification(warning('Error occured while File uploading!'));
              return;
            }
          }
        }
        const response = await saveMapping(data, candidateEntity, job, null, candidateData);

        handleSaveMappingResponse(response);
      } else {
        if (isCandidate !== 'PRESENT') {
          const response = await saveCandidate({
            email: data?.email,
            resume: candidateEntity?.mappedCandidateResumes
              ? (candidateEntity?.mappedCandidateResumes[0] as CandidateResume)
              : null,
          } as ICandidate);

          if (response?.data) {
            setNotification(success('Candidate is created successfully'));
            setCandidateEntity(response?.data);
            setOpenSubmissionForm(true);
          }
        } else if (isCandidate === 'PRESENT') {
          if (candidateResumes.length > 0) {
            try {
              await saveDocuments(candidateResumes);
              setNotification(success('File Uploaded Successfully!'));
              setOpenSubmissionForm(true);
            } catch (e) {
              console.error(e);
              setNotification(warning('Error occured while File uploading!'));
              setOpenSubmissionForm(false);
            }
          } else setOpenSubmissionForm(true);
        }
      }
    } catch (error: any) {
      if (error.response?.status === 409) {
        const message = error.response?.data ?? 'Candidate already exists.';
        setNotification(warning(message));
      } else {
        setNotification(failed(isSubmit ? 'Failed to save candidate' : 'Mapping failed'));
      }
    }
  };

  const handleSaveMappingResponse = (response: any) => {
    if (response?.status === 'success') {
      handleSuccessfulMapping(response);
    } else {
      handleMappingFailure(response);
    }
  };

  const handleSuccessfulMapping = (response: any) => {
    setNotification(success('Candidate is mapped to the selected job'));
    closeUpdatePanel();
    const url = `/candidate-layout/${'mappings'}?id=${
      response.response?.candidateId
    }&array=${JSON.stringify([])}`;
    navigate(url);
  };

  const handleMappingFailure = (response: any) => {
    const message = response?.response || '';
    if (message.includes('There is no accepted work assignment for the job')) {
      setNotification(
        failed(
          `This assigned job has not been accepted by the recruiter. 
          Please accept the job, then map the candidate.`
        )
      );
    } else if (response?.responseCode === 400) {
      setNotification(warning('Candidate is already mapped to this job'));
      closeUpdatePanel();
      job && cacheJob(job);
      if (showProfile) {
        openModalWithAction(response?.data.id, 'mapping', 'mapping', 'mapping');
      }
    } else if (response?.responseCode === 409) {
      setDisableSubmit(false);
      setNotification(failed(response?.data));
      return;
    } else {
      setNotification(failed('Mapping failed'));
    }
    setDisableSubmit(false);
  };

  const onUpload = async (file: File[], field: string) => {
    const url = await uploadFileToS3(file[0]);
    const resumeLink = url.split('?')[0];
    if (field === 'resume') {
      const alteredCandidate = candidateEntity ?? newMappingEntity();
      setCandidateResumes(
        alteredCandidate?.mappedCandidateResumes ? alteredCandidate?.mappedCandidateResumes : []
      );

      const alteredResumes = candidateResumes;
      alteredResumes.push({
        jobId: job?.id ?? '',
        candidateId: candidate?.candidateId ?? candidate?.id ?? candidateEntity?.candidateId ?? '',
        resumeFileName: file[0].name,
        resumeLocation: resumeLink,
        resumeUploadedOn: new Date(),
        resumeFileType: 'CV',
      });
      setCandidateResumes([...alteredResumes]);
      alteredCandidate.resumeLocation = resumeLink;
      alteredCandidate.resumeFileOriginalName = file[0].name;
      alteredCandidate.resumeUploadedOn = new Date();
      alteredCandidate.mappedCandidateResumes = alteredResumes;
      // setAllowReset(false);
      setIsResume(false);
      setCandidateEntity({ ...(alteredCandidate as ICandidate) });
    }
    if (field === 'otherDocuments') {
      const alteredCandidate = candidateEntity ?? newMappingEntity();
      const alteredDocuments = [...otherDocument];

      alteredDocuments.push({
        documentLocation: resumeLink,
        originalName: file[0].name,
        tag: null,
        uploadedOn: new Date(),
      });
      setOtherDocument([...alteredDocuments]);
      alteredCandidate.otherDocuments = alteredDocuments;
      setCandidateEntity({ ...(alteredCandidate as ICandidate) });
    }
  };

  const resumeAction = (action: RESUME_ACTIONS, url = '', docName?: string) => {
    switch (action) {
      case RESUME_ACTIONS.VIEW:
        if (url) {
          trackCV('view', url, cvViewSourcePage);
          setIsResumeURL(url);
        }
        setOpenCvView(true);
        break;
      case RESUME_ACTIONS.DOWNLOAD:
        if (!isEmpty(url)) {
          trackCV('download', url, cvViewSourcePage);
          url && downloadCV(url, docName);
        }
        break;
      case RESUME_ACTIONS.DELETE: {
        const alteredCandidate = (candidateEntity as ICandidate)
          ? { ...candidateEntity }
          : newMappingEntity();
        if (alteredCandidate?.mappedCandidateResumes) {
          const index = alteredCandidate?.mappedCandidateResumes?.findIndex(
            (resume) => resume.resumeLocation === url
          );
          if (index != -1) {
            alteredCandidate?.mappedCandidateResumes?.splice(index, 1);
            setCandidateResumes(alteredCandidate?.mappedCandidateResumes);
          }
          setCandidateEntity(alteredCandidate as ICandidate);
        }
        break;
      }
      case RESUME_ACTIONS.DELETE_OTHERS:
        if (candidateEntity) {
          const alteredCandidate = { ...candidateEntity };
          alteredCandidate.otherDocuments = alteredCandidate?.otherDocuments?.filter(
            (document) => document.documentLocation != url
          );
          setOtherDocument((prev) => {
            const newDocArray = prev.filter((prevDoc) => prevDoc.documentLocation != url);
            alteredCandidate.otherDocuments = newDocArray;
            return newDocArray;
          });
          setCandidateEntity({ ...alteredCandidate });
        }
        break;
    }
  };
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const amplitudeIntegration = () => {
    if (mapAsLead && isFrom === SourceType.JOBCRM_SMART_SOURCE_AI_SUGGESTIONS)
      amplitudeEventJobCrmSmartSourceAiMapAsLeadSave['Save'](loggedInUser);
    else if (mapAsLead && isFrom === SourceType.JOBCRM_SMART_SOURCE_SIMILAR_JOBS)
      amplitudeEventJobCrmSmartSourceSimilarJobsMapAsLeadSave['Save'](loggedInUser);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };
  return (
    <WsDrawer
      open={openDrawer}
      style={{ zIndex: '1300' }}
      hideHeader={true}
      onClose={() => null}
      size="medium"
      hideOverflow={true}
    >
      <Box>
        <HeaderAndCloseButtom
          closeAction={closeUpdatePanel}
          leftHeading={heading ?? 'Map as lead'}
          rightChildren={
            !hideJobSelection ? (
              <WidgetJobManagement selectedJob={job} ss={12} isFromMapAsLead={true} />
            ) : (
              <WsTypography variant={WsTypoVariants.BODY1} bold={true}>
                {`${job?.title} ( ${job?.jobCode} )`}
              </WsTypography>
            )
          }
        />
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onFormSubmit)} onKeyDown={handleKeyDown}>
            <Grid
              item
              xs={12}
              sx={{ height: '80vh', overflowY: 'auto', bgcolor: WsColors.TertiaryShades100 }}
            >
              {isSubmitting ? (
                <Grid>
                  <WsListingLoader>
                    <WsTypography variant={WsTypoVariants.TITLE} bold>
                      Parsing CV , Please wait..
                    </WsTypography>
                  </WsListingLoader>
                </Grid>
              ) : (
                <PersonalInfoNew
                  candidate={candidateEntity}
                  onUpload={(file: File[]) => onUpload(file, 'resume')}
                  purpose={isSubmit ? 'submission' : 'mapping'}
                  isCandidate={isCandidate}
                  resumeAction={resumeAction}
                />
              )}

              {isCandidate !== 'UNDEFINED' && (
                <ActionFooter>
                  <Grid item container xs={5.7} justifyContent="flex-end">
                    <Stack direction="row" spacing={2}>
                      <WsButton onClick={closeUpdatePanel}>Close</WsButton>
                      <WsButton type="submit" disabled={disableSubmit}>
                        {mapAsLead || isSubmit ? 'Next' : 'Update'}
                      </WsButton>
                    </Stack>
                  </Grid>
                </ActionFooter>
              )}
            </Grid>
          </form>
          {!isEmpty(candidateEntity?.mappedCandidateResumes) && (
            <CVViewModal
              open={openCvView}
              s3url={resumeURl}
              handleClose={() => setOpenCvView(false)}
            />
          )}
          {openSubmissionForm && candidateEntity && job && (
            <WsDrawer
              style={{ zIndex: '1300' }}
              size="medium"
              hideHeader={true}
              open={openSubmissionForm}
              onClose={() => {
                setOpenSubmissionForm(false);
                closeUpdatePanel();
              }}
              hideOverflow={true}
            >
              <MapAsSubmission
                selectedJob={job}
                selectedCandidate={candidateEntity}
                onClose={() => {
                  setOpenSubmissionForm(false);
                  closeUpdatePanel();
                }}
              />
            </WsDrawer>
          )}
        </FormProvider>
      </Box>
    </WsDrawer>
  );
};

export default UpdateProfileNew;
